import {useCallback, useEffect, useMemo, useState} from 'react';
import {QueryFunctionContext, useQuery} from 'react-query';
import {get} from 'lodash';

import DataTable from 'components/DataTable';
import ListActionsWrapper from 'components/ListActionsWrapper';
import ListWrapper from 'components/ListWrapper';
import PageHeader from 'components/PageHeader';
import SearchInput from 'components/SearchInput';
import Typography from 'components/Typography';
import ViewDetails from 'components/ViewDetails';
import ClickablePagination from 'components/ClickablePagination';
import api from 'api';
import {getFullName} from 'helpers/user';
import {ListResponse, Customer, DataItem, DataTableSchemaItem} from 'types';

import styles from './Customers.module.scss';
import {useHistory, useLocation, useRouteMatch} from 'react-router-dom';
import Button from 'components/Button';
import {format} from 'date-fns';
import useSort from 'hooks/useSort';
import useDebounce from 'hooks/useDebounce';
import FilterIcon from 'pages/Requests/FilterIcon';
import ListTabs from 'pages/UserForm/ListTabs';
import CheckIcon from 'pages/Requests/CheckIcon';
import Select from 'components/Select';
import {CompanyProperty} from 'types/property';
import {useCompanyConfig} from 'hooks/useCompanyConfig';
import classNames from 'classnames';
import NewCustomerModal from 'components/NewCustomerModal';
import CustomerFilterModal from 'components/CustomerFilterModal';
import SMSBlastModal from 'components/SMSBlastModal';

const TABS = [
  {title: 'All', value: 'all'},
  {title: 'To Invite', value: 'to_invite'},
  {title: 'Registered', value: 'registered'},
  {title: 'Active', value: 'active'},
];

export async function getUsers({queryKey}: QueryFunctionContext<any[]>) {
  const [, keyWord, order, page, metaDatas, status] = queryKey;
  const {data} = await api.get<ListResponse<Customer>>(`/customers/`, {
    params: {
      limit: 20,
      order,
      status,
      offset: keyWord ? 0 : +page * 20,
      search: keyWord ? keyWord : undefined,
      last_offering_date_after: metaDatas.last_offering_date.startDate
        ? format(metaDatas.last_offering_date.startDate, 'yyyy-MM-dd')
        : undefined,
      last_offering_date_before: metaDatas.last_offering_date.endDate
        ? format(metaDatas.last_offering_date.endDate, 'yyyy-MM-dd')
        : undefined,
      total_offerings_range_min: metaDatas.total_offerings_range.from
        ? metaDatas.total_offerings_range.from
        : undefined,
      total_offerings_range_max: metaDatas.total_offerings_range.to
        ? metaDatas.total_offerings_range.to
        : undefined,
      date_joined_after: metaDatas.dataJoined.startDate
        ? format(metaDatas.dataJoined.startDate, 'yyyy-MM-dd')
        : undefined,
      date_joined_before: metaDatas.dataJoined.endDate
        ? format(metaDatas.dataJoined.endDate, 'yyyy-MM-dd')
        : undefined,
      landlord_id: metaDatas?.landlord?.value
        ? metaDatas?.landlord?.value
        : undefined,
      property_id: metaDatas?.property?.value
        ? metaDatas?.property?.value
        : undefined,
      client_role: 'customers',
    },
  });

  return data;
}

async function getLandlordList() {
  const {data} = await api.get<ListResponse<CompanyProperty['landlord']>>(
    '/users/',
    {
      params: {role: 'landlord'},
    }
  );

  return data;
}

async function getCompanyProperties({queryKey}: QueryFunctionContext<any[]>) {
  const [, page] = queryKey;
  const {data} = await api.get<ListResponse<CompanyProperty>>(`/properties/`, {
    params: {
      limit: 20,
      page: +page * 20,
    },
  });

  return data;
}

function Customers() {
  const [status, setStatus] = useState(
    sessionStorage.getItem(`users_tab`) || 'all'
  );
  const [keyWord, setKeyWord] = useState(
    sessionStorage.getItem(`search_users`) || ''
  );
  const [isModalOpen, setModalOpen] = useState(
    get(useLocation().state, 'isAdd', false)
  );
  const {params} = useRouteMatch<{page: string | undefined}>();
  const {sortBy, sortOrder, order, onSort} = useSort('name');

  const [currentPage, setCurrentPage] = useState(parseInt(params.page || '1'));
  const [filterVisible, setFilterVisible] = useState(false);
  const [filter, setFilter] = useState({
    last_offering_date: {
      startDate: sessionStorage.getItem('last_offering_date_start')
        ? new Date(
            JSON.parse(sessionStorage.getItem('last_offering_date_start') || '')
          )
        : null,
      endDate: sessionStorage.getItem('last_offering_date_end')
        ? new Date(
            JSON.parse(sessionStorage.getItem('last_offering_date_end') || '')
          )
        : null,
    },
    total_offerings_range: {
      from: sessionStorage.getItem('total_offerings_range_from')
        ? JSON.parse(sessionStorage.getItem('total_offerings_range_from') || '')
        : null,
      to: sessionStorage.getItem('total_offerings_range_to')
        ? JSON.parse(sessionStorage.getItem('total_offerings_range_to') || '')
        : null,
    },
    dataJoined: {
      startDate: sessionStorage.getItem('data_joined_start')
        ? new Date(
            JSON.parse(sessionStorage.getItem('data_joined_start') || '')
          )
        : null,
      endDate: sessionStorage.getItem('data_joined_end')
        ? new Date(JSON.parse(sessionStorage.getItem('data_joined_end') || ''))
        : null,
    },
    landlord: sessionStorage.getItem('customer_landlord')
      ? JSON.parse(sessionStorage.getItem('customer_landlord') || '')
      : null,
    property: sessionStorage.getItem('customer_property')
      ? JSON.parse(sessionStorage.getItem('customer_property') || '')
      : null,
  });
  const [metaDatas, setMetaDatas] = useState<any>({
    last_offering_date: {
      startDate: sessionStorage.getItem('last_offering_date_start')
        ? new Date(
            JSON.parse(sessionStorage.getItem('last_offering_date_start') || '')
          )
        : null,
      endDate: sessionStorage.getItem('last_offering_date_end')
        ? new Date(
            JSON.parse(sessionStorage.getItem('last_offering_date_end') || '')
          )
        : null,
    },
    total_offerings_range: {
      from: sessionStorage.getItem('total_offerings_range_from')
        ? JSON.parse(sessionStorage.getItem('total_offerings_range_from') || '')
        : null,
      to: sessionStorage.getItem('total_offerings_range_to')
        ? JSON.parse(sessionStorage.getItem('total_offerings_range_to') || '')
        : null,
    },
    dataJoined: {
      startDate: sessionStorage.getItem('data_joined_start')
        ? new Date(
            JSON.parse(sessionStorage.getItem('data_joined_start') || '')
          )
        : null,
      endDate: sessionStorage.getItem('data_joined_end')
        ? new Date(JSON.parse(sessionStorage.getItem('data_joined_end') || ''))
        : null,
    },
    landlord: sessionStorage.getItem('customer_landlord')
      ? JSON.parse(sessionStorage.getItem('customer_landlord') || '')
      : null,
    property: sessionStorage.getItem('customer_property')
      ? JSON.parse(sessionStorage.getItem('customer_property') || '')
      : null,
  });
  const [selectedUsers, setSelectedUsers] = useState<any[]>([]);
  const [allSelected, setAllSelected] = useState(false);
  const [isMessageModalOpen, setMessageModalOpen] = useState(false);
  const {isPropertyManager, isMSPProvider, labels} = useCompanyConfig();
  const searchDebounced = useDebounce(keyWord, 400);

  const SCHEMA: DataTableSchemaItem[] = [
    {
      dataKey: 'checkbox',
      header:
        selectedUsers.length > 0 ? (
          <div
            onClick={(val) => {
              val.stopPropagation();
            }}
            className={styles.selectAll}
          >
            <div
              onClick={(val) => {
                setAllSelected(false);
                setSelectedUsers([]);
              }}
              className={styles.selectAllIcon}
            >
              <CheckIcon emptySelect={false} allSelected={allSelected} />
            </div>
            <Select
              components={{
                Input: () => null,
                ValueContainer: () => null,
              }}
              styles={{
                control: (base) => ({
                  ...base,
                  border: 'none',
                  backgroundColor: 'transparent',
                }),
                container: (base) => ({
                  ...base,
                  width: 70,
                }),
              }}
              options={[
                {label: 'All', value: 'all'},
                {label: 'None', value: 'none'},
              ]}
              onChange={(val) => {
                if (val?.value === 'all') {
                  setAllSelected(true);
                } else {
                  setSelectedUsers([]);
                  setAllSelected(false);
                }
              }}
            />
          </div>
        ) : (
          <div style={{height: 40}}></div>
        ),
      sortable: false,
      colWidth: '80px',
    },
    {
      dataKey: 'name',
      header: 'Name',
      sortable: true,
    },

    {
      dataKey: 'phone',
      header: 'Phone',
    },
    {
      dataKey: 'email',
      header: 'Email',
      colWidth: '250px',
    },
    {
      dataKey: 'customer_company',
      header: labels.company,
    },
    {
      dataKey: 'last_offering_date',
      header: 'Latest Order',
      sortable: true,
      colWidth: '130px',
    },
    {
      dataKey: 'date',
      header: 'Date Joined',
      sortable: true,
      colWidth: '130px',
    },
    {
      dataKey: 'total_offerings',
      header: 'Total Orders',
      align: 'center',
      sortable: true,
    },
    {
      dataKey: 'view',
      header: '',
    },
  ];

  const {
    data: users,
    isLoading,
    refetch,
  } = useQuery(
    [
      'users',
      searchDebounced,
      order,
      String(currentPage - 1 || 0),
      metaDatas,
      status,
    ],
    getUsers
  );
  const {data: landlordList} = useQuery(['landlords'], getLandlordList, {
    enabled: isPropertyManager || isMSPProvider,
  });

  const {data: properties} = useQuery(
    ['properties', currentPage - 1],
    getCompanyProperties,
    {enabled: isPropertyManager || isMSPProvider}
  );

  const landlordSelectOptions = useMemo(() => {
    if (!landlordList) return [];
    return landlordList?.results?.map((lan) => ({
      label: `${lan?.first_name} ${lan?.last_name}`,
      value: lan?.id,
    }));
  }, [landlordList]);

  const propertySelectOptions = useMemo(() => {
    if (!properties) return [];
    return properties?.results?.map((prop) => ({
      label: prop?.name,
      value: prop?.id,
    }));
  }, [properties]);

  useEffect(() => {
    if (params.page) {
      setCurrentPage(parseInt(params.page));
    }
  }, [params]);

  const history = useHistory();

  const handleNavigateUser = useCallback(
    (row: DataItem) => {
      history.push(`/people/customers/${row.id}/edit`);
    },
    [history]
  );

  const data = useMemo(
    function () {
      return users
        ? users.results.map((u) => ({
            ...u,
            name: getFullName(u.user),
            email: u.user.email,
            phone: u.user.phone,
            view: <ViewDetails to={`/people/customers/${u.id}/edit`} />,
            checkbox: (
              <div
                className={styles.checkbox}
                onClick={(val) => {
                  val.stopPropagation();
                  if (allSelected) {
                    setSelectedUsers(
                      users.results
                        .map((i) => ({id: i.id}))
                        .filter((i) => i.id !== u.id)
                    );
                    setAllSelected(false);
                    return;
                  }
                  const include = selectedUsers.find((i) => i.id === u.id);
                  if (include) {
                    setSelectedUsers(
                      selectedUsers.filter((i) => i.id !== u.id)
                    );
                  } else {
                    setSelectedUsers([...selectedUsers, {...u, id: u.id}]);
                  }
                }}
              >
                <CheckIcon
                  emptySelect={!selectedUsers.find((item) => item.id === u.id)}
                  allSelected={
                    selectedUsers.find((item) => item.id === u.id) ||
                    allSelected
                  }
                />
              </div>
            ),
          }))
        : [];
    },
    [users, selectedUsers, allSelected]
  );

  const handleModalClose = () => {
    setModalOpen(false);
  };

  const resetFilter = () => {
    setFilter({
      last_offering_date: {
        startDate: null,
        endDate: null,
      },
      total_offerings_range: {
        from: null,
        to: null,
      },
      dataJoined: {
        startDate: null,
        endDate: null,
      },
      landlord: null,
      property: null,
    });
    setMetaDatas({
      last_offering_date: {
        startDate: null,
        endDate: null,
      },
      total_offerings_range: {
        from: null,
        to: null,
      },
      dataJoined: {
        startDate: null,
        endDate: null,
      },
      landlord: null,
      property: null,
    });
    sessionStorage.removeItem('last_offering_date_start');
    sessionStorage.removeItem('last_offering_date_end');
    sessionStorage.removeItem('total_offerings_range_from');
    sessionStorage.removeItem('total_offerings_range_to');
    sessionStorage.removeItem('data_joined_start');
    sessionStorage.removeItem('data_joined_end');
    setFilterVisible(false);
  };

  const applyFilter = () => {
    if (filter.last_offering_date.startDate) {
      sessionStorage.setItem(
        'last_offering_date_start',
        JSON.stringify(filter.last_offering_date.startDate || '')
      );
    } else {
      sessionStorage.removeItem('last_offering_date_start');
    }
    if (filter.last_offering_date.endDate) {
      sessionStorage.setItem(
        'last_offering_date_end',
        JSON.stringify(filter.last_offering_date.endDate || '')
      );
    } else {
      sessionStorage.removeItem('last_offering_date_end');
    }
    if (filter.total_offerings_range.from) {
      sessionStorage.setItem(
        'total_offerings_range_from',
        JSON.stringify(filter.total_offerings_range.from || '')
      );
    } else {
      sessionStorage.removeItem('total_offerings_range_from');
    }
    if (filter.total_offerings_range.to) {
      sessionStorage.setItem(
        'total_offerings_range_to',
        JSON.stringify(filter.total_offerings_range.to || '')
      );
    } else {
      sessionStorage.removeItem('total_offerings_range_to');
    }
    if (filter.dataJoined.startDate) {
      sessionStorage.setItem(
        'data_joined_start',
        JSON.stringify(filter.dataJoined.startDate || '')
      );
    } else {
      sessionStorage.removeItem('data_joined_start');
    }
    if (filter.dataJoined.endDate) {
      sessionStorage.setItem(
        'data_joined_end',
        JSON.stringify(filter.dataJoined.endDate || '')
      );
    } else {
      sessionStorage.removeItem('data_joined_end');
    }
    if (filter.landlord) {
      sessionStorage.setItem(
        'customer_landlord',
        JSON.stringify(filter.landlord?.value || '')
      );
    } else {
      sessionStorage.removeItem('customer_landlord');
    }
    if (filter.property) {
      sessionStorage.setItem(
        'customer_property',
        JSON.stringify(filter.property?.value || '')
      );
    } else {
      sessionStorage.removeItem('customer_property');
    }

    setMetaDatas(filter);
    setCurrentPage(1);
    setFilterVisible(false);
  };

  const filtered = useMemo(() => {
    return Object.values(filter).some((obj) => {
      if (obj === null) return false;
      return Object.values(obj).some((value) => !!value);
    });
  }, [filter]);

  return (
    <>
      <PageHeader className={styles.top_header}>
        <Typography variant="h2">Customers</Typography>
        <div className={styles.count_wrapper}>
          <Typography textAlign="right" variant="h4">
            Total Customers: {users?.count}
          </Typography>
        </div>
      </PageHeader>
      <ListWrapper>
        <ListTabs
          tab="users_tab"
          tabs={TABS}
          activeTabValue={status}
          onChange={(val) => {
            setStatus(val);
            setCurrentPage(1);
          }}
        />
        <ListActionsWrapper className={styles.list_header}>
          <div className={styles.row}>
            <SearchInput
              value={keyWord}
              size="sm"
              onChange={(e) => {
                setKeyWord(e.target.value);
                setCurrentPage(1);
                sessionStorage.setItem(`search_users`, e.target.value);
              }}
            />
            <div
              className={classNames(styles.filterBtn, {
                [styles.filterBtnActive]: filtered,
              })}
              onClick={() => setFilterVisible(true)}
            >
              <FilterIcon isFilled={filtered} />
              <span>{filtered ? 'Filtered' : 'Filter'}</span>
            </div>
            {(selectedUsers.length > 0 || allSelected) && (
              <Button
                onClick={() => setMessageModalOpen(true)}
                color="blue"
                size="sm"
                className={styles.messageIcon}
              >
                SMS
              </Button>
            )}
          </div>
          <Button
            className={styles.button}
            size="sm"
            onClick={() => setModalOpen(true)}
          >
            Add New Customer
          </Button>
        </ListActionsWrapper>
        <DataTable
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={onSort}
          data={data}
          schema={SCHEMA}
          loading={isLoading}
          onClick={handleNavigateUser}
        />
        <ClickablePagination
          page={currentPage}
          total={Math.round((users?.count || 0) / 20)}
          setCurrentPage={setCurrentPage}
        />
      </ListWrapper>
      {isModalOpen && (
        <NewCustomerModal
          defaultClientRole="customers"
          showAdditionalPhoneFields={true}
          onSuccess={() => {
            refetch();
          }}
          isOpen={isModalOpen}
          onClose={handleModalClose}
        />
      )}
      {filterVisible && (
        <CustomerFilterModal
          isOpen={filterVisible}
          onClose={() => setFilterVisible(false)}
          applyFilter={applyFilter}
          filter={filter}
          landlordSelectOptions={landlordSelectOptions}
          propertySelectOptions={propertySelectOptions}
          resetFilter={resetFilter}
          setFilter={setFilter}
        />
      )}
      {isMessageModalOpen && (
        <SMSBlastModal
          isOpen={isMessageModalOpen}
          onClose={() => setMessageModalOpen(false)}
          count={allSelected ? users?.count : selectedUsers.length}
          customerIds={allSelected ? [] : selectedUsers.map((u) => u.id)}
          metaDatas={metaDatas}
        />
      )}
    </>
  );
}

export function formatDateString(inputDate: string) {
  const numbersOnly = inputDate.replace(/[^0-9]/g, '');
  const month = numbersOnly.slice(0, 2);
  const day = numbersOnly.slice(2, 4);
  const year = numbersOnly.slice(4, 8);

  let formattedDate = '';
  if (month) {
    if (parseInt(month) > 12) {
      formattedDate += `12`;
    } else {
      formattedDate += month;
    }
    if (day) {
      if (parseInt(day) > 31) {
        formattedDate += `/31`;
      } else {
        formattedDate += `/${day}`;
      }
      if (year) {
        if (parseInt(year) > 2025) {
          formattedDate += `/2025`;
        } else {
          formattedDate += `/${year}`;
        }
      }
    }
  }

  if (formattedDate.length > 10) {
    formattedDate = formattedDate.slice(0, 10);
  }

  return formattedDate;
}

export default Customers;
